import { SITE_SERVER_URL } from "@/shared/config/player";

export const R2_WORKER_URL = "https://bucket.torsunovoleggen.workers.dev";
const PERSONAL_DATA_WORKER_URL =
    "https://personal-data-worker.torsunovoleggen.workers.dev";

export interface BucketResponse<T = unknown> {
    success: boolean;
    data?: T;
    error?: string;
}

interface PaymentRecord {
    payment_id: string;
    amount: number;
    currency: string;
    status: string;
    email: string;
    timestamp: string;
    product: string;
}

export interface UserSessionData {
    [key: string]: unknown;
}

/**
 * Get data from bucket using session token (R2 Worker)
 * @param token - R2 session token
 * @param path - Optional path to fetch specific data (defaults to user session)
 */
/**
 * When the bucket worker rejects the token itself (401, or a 403 whose error
 * says so), the locally-cached `r2_session_token` is stale (rotated,
 * invalidated, or never properly issued during partial-login). Drop it so
 * subsequent `getToken() => null` short-circuit the lecture-sync calls — local
 * cache keeps working in the meantime, and the next full login (or
 * `reissueR2SessionToken` below) restores the token.
 *
 * 403 от воркера — это не только «токен мёртв»: так же он отвечает на чужой путь
 * (forbidden_path — watched_history.json до расширения USER_BUCKET_PATHS), на
 * «только для админов» и на запись под «войти как». Стирать токен на них значило
 * гасить всех его потребителей, включая журнал действий, до следующего входа.
 */
const TOKEN_REJECTED_403 = new Set([
    "not_authenticated",
    "session_expired",
    "Missing or invalid Bearer token",
    "Invalid or expired session token",
    "Session does not contain email information",
    "bad_or_expired_token",
    // в message рядом с error: "Forbidden"
    "Invalid or expired Bearer token",
    "Invalid or expired Bearer Token",
    "Invalid or missing Bearer Token",
]);

type ErrorBody = { error?: unknown; message?: unknown } | null | undefined;

const isTokenRejection = (status: number, body?: ErrorBody): boolean =>
    status === 401 ||
    (status === 403 &&
        (TOKEN_REJECTED_403.has(String(body?.error ?? "")) ||
            TOKEN_REJECTED_403.has(String(body?.message ?? ""))));

const handleBucketAuthFailure = (status: number, body?: ErrorBody): void => {
    if (isTokenRejection(status, body)) {
        try { localStorage.removeItem("r2_session_token"); } catch {}
    }
};

export const getBucketData = async <T = unknown>(
    token: string,
    path: string = "/api/userSession",
): Promise<BucketResponse<T>> => {
    try {
        const response = await fetch(`${R2_WORKER_URL}${path}`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            handleBucketAuthFailure(response.status, data);
            return {
                success: false,
                error: (data as { error?: string }).error || `bucket GET failed (${response.status})`,
            };
        }

        const data = await response.json();
        return {
            success: true,
            data: data as T,
        };
    } catch (err) {
        return {
            success: false,
            error:
                err instanceof Error
                    ? err.message
                    : "Failed to fetch bucket data",
        };
    }
};

/**
 * Save JSON data to bucket using session token (R2 Worker)
 * @param token - R2 session token
 * @param data - JSON data to save
 * @param path - Optional path to save data (defaults to user session)
 */
export const saveBucketData = async <T = unknown>(
    token: string,
    data: UserSessionData,
    path: string = "/api/userSession",
): Promise<BucketResponse<T>> => {
    try {
        const response = await fetch(`${R2_WORKER_URL}${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const responseData = await response.json().catch(() => ({}));
            handleBucketAuthFailure(response.status, responseData);
            return {
                success: false,
                error: (responseData as { error?: string }).error || `bucket POST failed (${response.status})`,
            };
        }

        const responseData = await response.json();
        return {
            success: true,
            data: responseData as T,
        };
    } catch (err) {
        return {
            success: false,
            error:
                err instanceof Error
                    ? err.message
                    : "Failed to save bucket data",
        };
    }
};

/**
 * Re-issue an r2_session_token using the email_id_token that was
 * stashed at login time. Returns true on success (token written to
 * localStorage) or false if no email_id_token is available / the
 * worker rejected.
 *
 * Called at app boot when the user looks logged in (auth_token /
 * site_user_email present) but r2_session_token is missing — the
 * cause of the 403 spam on lecture-sync endpoints. Also called in
 * response to a 401/403 caught above.
 */
export const reissueR2SessionToken = async (): Promise<boolean> => {
    try {
        const idToken = localStorage.getItem("email_id_token");
        if (!idToken) return false;
        const res = await fetch(`${R2_WORKER_URL}/auth/email`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_token: idToken }),
        });
        if (!res.ok) return false;
        const data = (await res.json()) as { session_token?: string };
        if (!data.session_token) return false;
        localStorage.setItem("r2_session_token", data.session_token);
        return true;
    } catch {
        return false;
    }
};

/**
 * Boot-time gap-filler. If the user appears logged in (`auth_token`
 * is set) but `r2_session_token` is missing, attempt to reissue it
 * silently. Safe to call once on app mount — no-ops when nothing to
 * fix or when the email_id_token isn't cached (older sessions).
 */
export const ensureR2SessionToken = async (): Promise<void> => {
    try {
        if (localStorage.getItem("r2_session_token")) return;
        if (!localStorage.getItem("auth_token")) return;
        await reissueR2SessionToken();
    } catch {
        /* best-effort, never blocks the app */
    }
};

/** The live session token, newest key first; the r2 key is a legacy fallback. */
export const getSessionToken = (): string => {
    try {
        return (
            localStorage.getItem("personal_data_session_token") ||
            localStorage.getItem("auth_token") ||
            localStorage.getItem("r2_session_token") ||
            ""
        );
    } catch {
        return "";
    }
};

/**
 * Reissue even when a token is already stored.
 *
 * [ensureR2SessionToken] tests PRESENCE, so a token that is present but dead is
 * never replaced — the state that made a real group member read as a stranger
 * with an empty conversation, permanently, across every refresh. Dropping the
 * stale value first is what lets the reissue actually run.
 */
export const forceReissueR2SessionToken = async (): Promise<boolean> => {
    try {
        localStorage.removeItem("r2_session_token");
    } catch {
        /* private mode */
    }
    return reissueR2SessionToken();
};

/**
 * fetch with the session attached, which heals itself once.
 *
 * A 401/403 used to be swallowed by the callers as an empty list, so an expired
 * token looked exactly like "you belong to no groups" and "there are no messages".
 * Here the dead token is thrown away, a fresh one minted, and the call retried a
 * single time; only a genuine second failure reaches the caller.
 */
export const authedFetch = async (
    url: string,
    init: RequestInit = {},
): Promise<Response> => {
    const send = (token: string) =>
        fetch(url, {
            ...init,
            headers: {
                "Content-Type": "application/json",
                ...(init.headers || {}),
                Authorization: `Bearer ${token}`,
            },
        });

    let res = await send(getSessionToken());
    if (res.status !== 401 && res.status !== 403) return res;
    if (res.status === 403) {
        // «Не участник», «не приглашён» и подобные 403 новым токеном не лечатся, а
        // перевыпуск сначала стирает токен. Голое «Forbidden» воркер отдаёт и
        // мёртвому токену, и не-админу — его лечим, как раньше.
        const body = (await res.clone().json().catch(() => null)) as ErrorBody;
        const ambiguous = !body || (body.error === "Forbidden" && !body.message);
        if (!ambiguous && !isTokenRejection(403, body)) return res;
    }
    const reissued = await forceReissueR2SessionToken();
    if (!reissued) return res;
    res = await send(getSessionToken());
    return res;
};

/**
 * Save JSON data to Site Server using session token
 * @param sessionToken - Site server session token
 * @param data - JSON data to save
 */
export const saveSiteServerData = async <T = unknown>(
    sessionToken: string,
    data: UserSessionData,
): Promise<BucketResponse<T>> => {
    try {
        const response = await fetch(
            `${SITE_SERVER_URL}/api/auth/save-session-data`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    session_token: sessionToken,
                    data: data,
                }),
            },
        );

        const responseData = await response.json();

        if (!response.ok || responseData.status !== "success") {
            return {
                success: false,
                error: responseData.message || "Failed to save to site server",
            };
        }

        return {
            success: true,
            data: responseData as T,
        };
    } catch (err) {
        return {
            success: false,
            error:
                err instanceof Error
                    ? err.message
                    : "Failed to save to site server",
        };
    }
};

/**
 * Uploads payment data to the bucket via R2 worker
 * Path: {email}/site_user_data/avatar_payments.json
 */
export const uploadPaymentToBucket = async (
    sessionToken: string,
    userEmail: string,
    paymentData: PaymentRecord,
): Promise<boolean> => {
    // Use R2 worker endpoint to save data to user's folder
    const response = await fetch(`${R2_WORKER_URL}/api/save-data`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
            file_path: "site_user_data/avatar_payments.json",
            data: paymentData,
        }),
    });

    const responseData = await response.json();

    if (!response.ok || !responseData.success) {
        throw new Error(responseData.message || "Failed to save payment data");
    }

    return true;
};
